import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Row, Col, Spinner, Modal, Button } from 'react-bootstrap';
import { useNavigate, Routes, Route, useLocation } from 'react-router-dom';

// Import components
import TopUp from './Main/TopUp';
import Withdraw from './Main/Withdraw';
import ReportsPage from './Main/ReportsPage';
import MessagesPage from './Main/MessagesPage';
import SettingsPage from './Main/SettingsPage';
import History from './Main/History';
import Plan from './Main/Plan'; 
import Home from './Main/Home';
import NavbarComponent from '../Components/Navbar';
import Sidebar from '../Components/Sidebar';
import ReferralCode from './Main/ReferralCode';
import DailyTask from './Main/DailyTask';
import StakingRoom from './Main/StakingRoom';

function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();  // Hook to track route changes
  const [user, setUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);  // Sidebar visibility state
  const [isMobile, setIsMobile] = useState(false); // State to track screen size

  // Check screen size to toggle sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    };

    // Set initial state based on the current window width
    handleResize();
    
    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    } else {
      navigate('/'); // Redirect to login if no user is found
    }
  }, [navigate]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (user) {
        try {
          setLoading(true);
          const response = await axios.get('https://www.sharedvision-partners.online/v1/API/get_user.php', {
            params: { email: user.email },
          });
          

          if (response.data.success) {
            setUserDetails(response.data.user);
          } else {
            setError('User details not found');
          }
        } catch (error) {
          setError('Error fetching user details');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchUserDetails();
  }, [user]);

  // Close sidebar when navigating on mobile
  useEffect(() => {
    if (isMobile) {
      setIsSidebarVisible(false);  // Close sidebar when route changes
    }
  }, [location.pathname, isMobile]);  // Triggered on route change

  const handleLogout = () => {
    localStorage.removeItem('user');
    setShowLogoutModal(false); // Close the modal
    navigate('/'); // Navigate to login page
  };

  const handleCancelLogout = () => {
    setShowLogoutModal(false); // Close the modal
  };

  // Toggle sidebar visibility (for mobile view)
  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  if (loading) return <Spinner animation="border" />;
  if (error) return <div>Error: {error}</div>;

  return (
    <>
      <NavbarComponent 
        userDetails={userDetails} 
        setShowLogoutModal={setShowLogoutModal} 
        isMobile={isMobile} 
        toggleSidebar={toggleSidebar} // Pass toggleSidebar function to Navbar
      />

      <Container fluid className="p-4">
        <Row>
          {/* Show Sidebar only on large screens, otherwise show hamburger menu */}
          {!isMobile && (
            <Col md={3} className="d-none d-md-block">
              <Sidebar userDetails={userDetails} />
            </Col>
          )}

          {/* On small screens, show sidebar content as a toggle */}
          {isMobile && isSidebarVisible && (
            <Col xs={12} className="d-md-none">
              <Sidebar userDetails={userDetails} />
            </Col>
          )}

          <Col md={9} xs={12}>
            <Routes>
              <Route path="/" element={<Home userDetails={userDetails} />} />
              <Route path="/reports" element={<ReportsPage userDetails={userDetails} />} />
              <Route path="/messages" element={<MessagesPage userDetails={userDetails}/>} />
              <Route path="/settings" element={<SettingsPage userDetails={userDetails}/>} />
              <Route path="/history" element={<History userDetails={userDetails}/>} />
              <Route path="/topup" element={<TopUp userDetails={userDetails} />} />
              <Route path="/withdraw" element={<Withdraw userDetails={userDetails}/>} />
              <Route path="/plan" element={<Plan userDetails={userDetails} />} />
              <Route path="/referral" element={<ReferralCode userDetails={userDetails} />} />
              <Route path="/dailytask" element={<DailyTask userDetails={userDetails} />} />
              <Route path="/stakingroom" element={<StakingRoom userDetails={userDetails} />} />
              <Route path="*" element={<div>Page Not Found</div>} />
            </Routes>
          </Col>
        </Row>
      </Container>

      <Modal show={showLogoutModal} onHide={handleCancelLogout}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Logout</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to log out?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelLogout}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleLogout}>
            Logout
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default Dashboard;
